import { FC, useEffect, useState } from "react"
import { SettingMultiMeetings, SettingMultiMerchants, SettingMultiPipelines, SettingMultiReimbursements } from "../../interfaces/Settings"
import { useSettingsStore } from "../../stores/SettingsStore"
import { useIntl } from "react-intl"
import { SelectOption, SelectOptionMerchantLink, SelectOptionWithIcon } from "../../interfaces/SelectOption"
import { PreferencePipeline } from "./Pipeline/PreferencePipeline"
import { CustomFieldPipeline } from "./Pipeline/CustomFieldPipeline"
import { PreferenceMeeting } from "./Meeting/PreferenceMeeting"
import { CustomFieldMeeting } from "./Meeting/CustomFieldMeeting"
import useDragAndDropDatabase from "../../hooks/useDragAndDropDatabase"
import { DragAndDropDatabase } from "../DragAndDrop/Database"
import { closestCenter, DndContext } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import useUser from "../../hooks/User"
import { usePipelineStore } from "../../stores/PipelineStore"
import { useMeetingStore } from "../../stores/MeetingsStore"
import { useMerchantStore } from "../../stores/MerchantStore"
import useModalEffect from "../../hooks/useModalEffect"
import { DeleteTemplatePipeline } from "./Pipeline/DeleteTemplatePipeline"
import { AddTemplatePipeline } from "./Pipeline/AddTemplatePipeline"
import { AddTemplateMeeting } from "./Meeting/AddTemplateMeeting"
import { DeleteTemplateMeeting } from "./Meeting/DeleteTemplateMeeting"
import { PreferenceMerchant } from "./Merchant/PreferenceMerchant"
import { CustomFieldMerchant } from "./Merchant/CustomFieldMerchant"
import { AddTemplateMerchant } from "./Merchant/AddTemplateMerchant"
import { DeleteTemplateMerchant } from "./Merchant/DeleteTemplateMerchant"
import { GroupBase } from "react-select"
import { AddTemplateReimbursement } from "./Reimbursement/AddTemplateReimbursement"
import { DeleteTemplateReimbursement } from "./Reimbursement/DeleteTemplateReimbursement"
import { PreferenceReimbursement } from "./Reimbursement/PreferenceReimbursement"
import { CustomFieldReimbursement } from "./Reimbursement/CustomFieldReimbursement"
import { useReimbursementStore } from "../../stores/ReimbursementStore"

interface Props<T extends SettingMultiMeetings | SettingMultiMerchants | SettingMultiPipelines | SettingMultiReimbursements> {
    name: string
    type: string
    listTemplate: T[]
    selectTemplate: T
    data: T
    dataPipeline: SelectOption[]
    dataMerchant: SelectOption[]
    dataMeeting: SelectOption[]
    dataProgres: SelectOption[]
    dataProduct: SelectOption[]
    dataRole: SelectOption[]
    optionType: GroupBase<SelectOptionWithIcon>[]
    optionsResetSerial: SelectOption[]
    optionMerchantLink: SelectOptionMerchantLink[]
    isLoading: boolean
    setSelectTemplate: React.Dispatch<React.SetStateAction<T>>
    dataSource?: SelectOption[]
}

const ListDatabase = <T extends SettingMultiMeetings | SettingMultiMerchants | SettingMultiPipelines | SettingMultiReimbursements>({ name, type, data, dataPipeline, dataMerchant, dataMeeting, dataProgres, dataProduct, dataRole, dataSource, optionType, optionsResetSerial, optionMerchantLink, listTemplate, selectTemplate, isLoading, setSelectTemplate }: Props<T>) => {
    const intl = useIntl()
    const user = useUser()
    const { multiMeetings } = useMeetingStore();
    const { multiReimbursements } = useReimbursementStore();
    const { multiPipelines } = usePipelineStore();
    const { multiMerchants } = useMerchantStore();

    const [isAddPipelineModal, setIsAddPipelineModal] = useState<boolean>(false)
    const [isAddMeetingModal, setIsAddMeetingModal] = useState<boolean>(false)
    const [isAddMerchantModal, setIsAddMerchantModal] = useState<boolean>(false)
    const [isAddReimbursementModal, setIsAddReimbursementModal] = useState<boolean>(false)
    const [isDeletePipelineModal, setIsDeletePipelineModal] = useState<boolean>(false)
    const [isDeleteMeetingModal, setIsDeleteMeetingModal] = useState<boolean>(false)
    const [isDeleteMerchantModal, setIsDeleteMerchantModal] = useState<boolean>(false)
    const [isDeleteReimbursementModal, setIsDeleteReimbursementModal] = useState<boolean>(false)
    const [database, setDatabase] = useState<T>({} as T)
    const [selectTab, setSelectTab] = useState<string>('preference')
    const [mode, setMode] = useState<string>('normal')
    const listTab = [
        {
            id: 'preference',
            name: intl.formatMessage({ id: 'FORM.LABEL.PREFERENCES' }, { title: '' }),
            target: '#tab_preference'
        },
        {
            id: 'customField',
            name: intl.formatMessage({ id: "FORM.LABEL.CUSTOM_COLUMN" }),
            target: '#tab_customField'
        }
    ]

    const { updatedData: updatedDataTemplate, setType, isLoading: isLoadingTemplate, setUpdatedData: setUpdateDataTemplate, handleDragEnd: handleDragEndTemplate, sensors: sensorsTemplate } = useDragAndDropDatabase();

    const handleSelectDatabase = (item: T) => {
        setSelectTemplate(item)
    }

    const handleAddDatabase = () => {
        if (type === 'pipeline') setIsAddPipelineModal(true)
        else if (type === 'meeting') setIsAddMeetingModal(true)
        else if (type === 'merchant') setIsAddMerchantModal(true)
        else if (type === 'reimbursement') setIsAddReimbursementModal(true)
    }

    const handleDeleteDatabase = (item: T) => {
        setDatabase(item)

        if (type === 'pipeline') setIsDeletePipelineModal(true)
        else if (type === 'meeting') setIsDeleteMeetingModal(true)
        else if (type === 'merchant') setIsDeleteMerchantModal(true)
        else if (type === 'reimbursement') setIsDeleteReimbursementModal(true)
    }

    const handleMode = (btn: string) => {
        if (btn === 'btnEdit') {
            setMode('edit')
        } else if (btn === 'btnClose') {
            setMode('normal')
        }
    }

    const isShowBtnAdd = () => {
        if (type === 'pipeline' && multiPipelines.length < user.data.companyFeatureLimitPipelineTemplate) {
            return true
        } else if (type === 'merchant' && multiMerchants.length < user.data.companyFeatureLimitMerchantTemplate) {
            return true
        } else if (type === 'meeting' && multiMeetings.length < user.data.companyFeatureLimitMeetingTemplate) {
            return true
        } else if (type === 'reimbursement' && multiReimbursements.length < user.data.companyFeatureLimitReimbursementTemplate) {
            return true
        } else return false
    }

    useEffect(() => {
        setUpdateDataTemplate(listTemplate)
    }, [listTemplate])

    useEffect(() => {
        setType(type)
    }, [type])

    useModalEffect({
        showModal: isAddPipelineModal,
        setShowModal: setIsAddPipelineModal,
        modalId: `add-database-pipeline-modal`
    })

    useModalEffect({
        showModal: isAddMeetingModal,
        setShowModal: setIsAddMeetingModal,
        modalId: `add-database-meeting-modal`
    })

    useModalEffect({
        showModal: isAddMerchantModal,
        setShowModal: setIsAddMerchantModal,
        modalId: `add-database-merchant-modal`
    })

    useModalEffect({
        showModal: isAddReimbursementModal,
        setShowModal: setIsAddReimbursementModal,
        modalId: `add-database-reimbursement-modal`
    })

    useModalEffect({
        showModal: isDeletePipelineModal,
        setShowModal: setIsDeletePipelineModal,
        modalId: `delete-database-pipeline-modal`
    })

    useModalEffect({
        showModal: isDeleteMeetingModal,
        setShowModal: setIsDeleteMeetingModal,
        modalId: `delete-database-meeting-modal`
    })

    useModalEffect({
        showModal: isDeleteMerchantModal,
        setShowModal: setIsDeleteMerchantModal,
        modalId: `delete-database-merchant-modal`
    })

    useModalEffect({
        showModal: isDeleteReimbursementModal,
        setShowModal: setIsDeleteReimbursementModal,
        modalId: `delete-database-reimbursement-modal`
    })

    return (
        <>
            <div className="row g-7">
                <div className="col-lg-6 col-xl-4">
                    <div className="card card-flush">
                        <div className="card-header pt-7">
                            <div className="card-title m-0 w-100 d-flex align-items-center justify-content-between">
                                <h2>Database {name}</h2>
                                {
                                    mode === 'normal' &&
                                    <button onClick={() => handleMode('btnEdit')} type="button" className="btn btn-sm btn-icon btn-active-color-primary btn-active-light-primary">
                                        <span className="svg-icon svg-icon-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path opacity="0.3" fill-rule="evenodd" clip-rule="evenodd" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black" />
                                                <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black" />
                                                <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black" />
                                            </svg>
                                        </span>
                                    </button>
                                }
                                {
                                    isLoadingTemplate &&
                                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                }
                            </div>
                        </div>
                        <div className="card-body pt-5">
                            {
                                updatedDataTemplate.length === 0 ?
                                    <div className="loading-overlay">
                                        <div className="loader"></div>
                                    </div>
                                    :
                                    <>
                                        <div className="d-flex flex-column gap-5" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                            {
                                                <DndContext onDragEnd={handleDragEndTemplate} sensors={sensorsTemplate} collisionDetection={closestCenter}>
                                                    <SortableContext items={updatedDataTemplate as any} strategy={verticalListSortingStrategy}>
                                                        {
                                                            updatedDataTemplate.map((data: T, index: number) => {
                                                                return (
                                                                    <DragAndDropDatabase
                                                                        id={data.id}
                                                                        data={data}
                                                                        selectTemplate={selectTemplate}
                                                                        mode={mode}
                                                                        handleSelectDatabase={handleSelectDatabase}
                                                                        handleDeleteDatabase={handleDeleteDatabase}
                                                                    />
                                                                )
                                                            })
                                                        }
                                                    </SortableContext>
                                                </DndContext>
                                            }
                                        </div>
                                        {
                                            mode === 'normal' ?
                                                isShowBtnAdd() &&
                                                <>
                                                    <div className="separator my-7"></div>
                                                    <button type="button" className="btn btn-primary w-100" onClick={handleAddDatabase}>
                                                        <i className="fa-plus fas"></i> {intl.formatMessage({ id: 'FORM.ACTION.ADD' })} Database
                                                    </button>
                                                </>
                                                :
                                                <>
                                                    <div className="separator my-7"></div>
                                                    <button type="button" className="w-100 btn btn-secondary" onClick={() => handleMode('btnClose')}>
                                                        {intl.formatMessage({ id: 'FORM.ACTION.CLOSE' })}
                                                    </button>
                                                </>
                                        }
                                    </>
                            }
                        </div>
                    </div>
                </div>

                <div className="col-xl-8">
                    <div className="card card-flush" id="kt_contacts_main">
                        <div className="card-body pt-5">
                            <ul className="nav nav-custom nav-tabs nav-line-tabs nav-line-tabs-2x fs-6 fw-bold mb-8">
                                {
                                    listTab.map((item, index) => (
                                        <li className="nav-item" onClick={() => setSelectTab(item.id)}>
                                            <a className={`nav-link text-active-primary pb-4 ${selectTab === item.id ? 'active' : ''}`} data-bs-toggle="tab" href={item.target} >
                                                {item.name}
                                            </a>
                                        </li>
                                    ))
                                }
                            </ul>
                            <div className="tab-content" id="">
                                <div className={`tab-pane fade ${selectTab === 'preference' ? 'show active' : ''}`} id="kt_contact_view_general" role="tabpanel">
                                    {
                                        isLoading ?
                                            <div className="loading-overlay">
                                                <div className="loader"></div>
                                            </div>
                                            :
                                            type === 'pipeline' ? <PreferencePipeline data={data as SettingMultiPipelines} dataMerchant={dataMerchant} dataProgres={dataProgres} dataProduct={dataProduct} dataSource={dataSource ?? []} dataRole={dataRole} />
                                                : type === 'meeting' ? <PreferenceMeeting data={data as SettingMultiMeetings} dataMerchant={dataMerchant} />
                                                    : type === 'merchant' ? <PreferenceMerchant data={data as SettingMultiMerchants} dataPipeline={dataPipeline} dataMerchant={dataMerchant} dataMeeting={dataMeeting} optionMerchantLink={optionMerchantLink} />
                                                        : type === 'reimbursement' ? <PreferenceReimbursement data={data as SettingMultiReimbursements} dataRole={dataRole} />
                                                            : <></>
                                    }
                                </div>
                                <div className={`tab-pane fade ${selectTab === 'customField' ? 'show active' : ''}`} id="kt_contact_view_meetings" role="tabpanel">
                                    {
                                        isLoading ?
                                            <div className="loading-overlay">
                                                <div className="loader"></div>
                                            </div>
                                            :
                                            type === 'pipeline' ? <CustomFieldPipeline data={data as SettingMultiPipelines} dataPipeline={dataPipeline} dataMerchant={dataMerchant} dataMeeting={dataMeeting} optionType={optionType} optionsResetSerial={optionsResetSerial} />
                                                : type === 'meeting' ? <CustomFieldMeeting data={data as SettingMultiMeetings} dataPipeline={dataPipeline} dataMerchant={dataMerchant} dataMeeting={dataMeeting} optionType={optionType} optionsResetSerial={optionsResetSerial} />
                                                    : type === 'merchant' ? <CustomFieldMerchant data={data as SettingMultiMerchants} dataPipeline={dataPipeline} dataMerchant={dataMerchant} dataMeeting={dataMeeting} optionType={optionType} optionsResetSerial={optionsResetSerial} />
                                                        : type === 'reimbursement' ? <CustomFieldReimbursement data={data as SettingMultiReimbursements} dataPipeline={dataPipeline} dataMerchant={dataMerchant} dataMeeting={dataMeeting} optionType={optionType} optionsResetSerial={optionsResetSerial} />
                                                            : <></>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {
                isAddPipelineModal && <AddTemplatePipeline dataRole={dataRole} />
            }
            {
                isAddMeetingModal && <AddTemplateMeeting />
            }
            {
                isAddMerchantModal && <AddTemplateMerchant />
            }
            {
                isAddReimbursementModal && <AddTemplateReimbursement />
            }

            {
                isDeletePipelineModal && <DeleteTemplatePipeline item={database as SettingMultiPipelines} selectTemplate={selectTemplate as SettingMultiPipelines} setSelectTemplate={setSelectTemplate as React.Dispatch<React.SetStateAction<SettingMultiPipelines>>} />
            }
            {
                isDeleteMeetingModal && <DeleteTemplateMeeting item={database as SettingMultiMeetings} selectTemplate={selectTemplate as SettingMultiMeetings} setSelectTemplate={setSelectTemplate as React.Dispatch<React.SetStateAction<SettingMultiMeetings>>} />
            }
            {
                isDeleteMerchantModal && <DeleteTemplateMerchant item={database as SettingMultiMerchants} selectTemplate={selectTemplate as SettingMultiMerchants} setSelectTemplate={setSelectTemplate as React.Dispatch<React.SetStateAction<SettingMultiMerchants>>} />
            }
            {
                isDeleteReimbursementModal && <DeleteTemplateReimbursement item={database as SettingMultiReimbursements} selectTemplate={selectTemplate as SettingMultiReimbursements} setSelectTemplate={setSelectTemplate as React.Dispatch<React.SetStateAction<SettingMultiReimbursements>>} />
            }
        </>
    )
}

export { ListDatabase }